import React, { useState, useEffect } from 'react';
import { X, UserPlus, Shield, AlertCircle, CheckCircle } from 'lucide-react';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import { roleService } from '../../../services/roleService';
import organizationService from '../../../services/organizationService';
import { useAuth } from '../../../contexts/AuthContext';

export default function AddMemberModal({ isOpen, onClose, tenantId, tenantName, onMemberAdded }) {
  const { profile } = useAuth();
  const [formData, setFormData] = useState({
    email: '',
    firstName: '',
    lastName: '',
    roleId: '',
    jobTitle: ''
  });
  const [roles, setRoles] = useState([]);
  const [availableRoles, setAvailableRoles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [validatingRole, setValidatingRole] = useState(false);

  useEffect(() => {
    if (isOpen) {
      loadRoles();
      resetForm();
    }
  }, [isOpen, tenantId]);

  const loadRoles = async () => {
    try {
      const allRoles = await roleService?.getAllRoles();
      setRoles(allRoles || []);
      
      // Filter roles based on user's permission to invite
      await filterAvailableRoles(allRoles);
    } catch (error) {
      console.error('Error loading roles:', error);
      setError('Failed to load roles');
    }
  };

  const filterAvailableRoles = async (allRoles) => {
    if (!allRoles || allRoles?.length === 0) return;

    setValidatingRole(true);
    try {
      const filtered = [];
      
      for (const role of allRoles) {
        const canInvite = await organizationService?.canInviteToRole(
          role?.id,
          tenantId
        );
        
        if (canInvite) {
          filtered?.push(role);
        }
      }
      
      setAvailableRoles(filtered);
      
      // Auto-select first available role
      if (filtered?.length > 0 && !formData?.roleId) {
        setFormData(prev => ({ ...prev, roleId: filtered?.[0]?.id }));
      }
    } catch (error) {
      console.error('Error filtering roles:', error);
      setAvailableRoles(allRoles); // Fallback to all roles
    } finally {
      setValidatingRole(false);
    }
  };

  const resetForm = () => {
    setFormData({
      email: '',
      firstName: '',
      lastName: '',
      roleId: '',
      jobTitle: ''
    });
    setError('');
    setSuccess('');
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    setError('');
    setSuccess('');

    // Validation
    if (!formData?.email || !formData?.firstName || !formData?.lastName || !formData?.roleId) {
      setError('Please fill in all required fields');
      return;
    }

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex?.test(formData?.email)) {
      setError('Please enter a valid email address');
      return;
    }

    setLoading(true);

    try {
      const result = await organizationService?.createMember({
        email: formData?.email,
        firstName: formData?.firstName,
        lastName: formData?.lastName,
        roleId: formData?.roleId,
        tenantId: tenantId,
        jobTitle: formData?.jobTitle
      });

      if (result?.success) {
        setSuccess(result?.message || 'Member added successfully');
        
        // Notify parent component
        onMemberAdded?.();
        
        // Close modal after 2 seconds
        setTimeout(() => {
          onClose();
        }, 2000);
      }
    } catch (error) {
      console.error('Error creating member:', error);
      setError(error?.message || 'Failed to add member');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setError('');
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
              <UserPlus className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-gray-900">Add New Member</h2>
              <p className="text-sm text-gray-600 mt-0.5">
                {tenantName || 'Organization'}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Success Message */}
          {success && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-4 flex items-start gap-3">
              <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="text-sm font-medium text-green-900">{success}</p>
              </div>
            </div>
          )}

          {/* Error Message */}
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="text-sm font-medium text-red-900">{error}</p>
              </div>
            </div>
          )}

          {/* Email */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Email Address <span className="text-red-500">*</span>
            </label>
            <Input
              type="email"
              value={formData?.email}
              onChange={(e) => handleChange('email', e?.target?.value)}
              placeholder="member@example.com"
              required
              disabled={loading}
            />
          </div>

          {/* Name Fields */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                First Name <span className="text-red-500">*</span>
              </label>
              <Input
                type="text"
                value={formData?.firstName}
                onChange={(e) => handleChange('firstName', e?.target?.value)}
                placeholder="John"
                required
                disabled={loading}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Last Name <span className="text-red-500">*</span>
              </label>
              <Input
                type="text"
                value={formData?.lastName}
                onChange={(e) => handleChange('lastName', e?.target?.value)}
                placeholder="Doe"
                required
                disabled={loading}
              />
            </div>
          </div>

          {/* Job Title */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Job Title
            </label>
            <Input
              type="text"
              value={formData?.jobTitle}
              onChange={(e) => handleChange('jobTitle', e?.target?.value)}
              placeholder="Sales Manager"
              disabled={loading}
            />
          </div>

          {/* Role Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Role <span className="text-red-500">*</span>
            </label>
            {validatingRole ? (
              <div className="flex items-center gap-2 text-sm text-gray-600 py-2">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                <span>Loading available roles...</span>
              </div>
            ) : availableRoles?.length === 0 ? (
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 flex items-start gap-3">
                <Shield className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <p className="text-sm font-medium text-yellow-900">
                    No roles available
                  </p>
                  <p className="text-sm text-yellow-700 mt-1">
                    You can only invite members to roles below your current level.
                  </p>
                </div>
              </div>
            ) : (
              <>
                <Select
                  value={formData?.roleId}
                  onChange={(e) => handleChange('roleId', e?.target?.value)}
                  required
                  disabled={loading}
                >
                  <option value="">Select a role</option>
                  {availableRoles?.map(role => (
                    <option key={role?.id} value={role?.id}>
                      {role?.displayName} (Level {role?.hierarchyLevel})
                    </option>
                  ))}
                </Select>
                <p className="text-xs text-gray-500 mt-2">
                  You can only assign roles at or below your current role level
                </p>
              </>
            )}
          </div>

          {/* Info Box */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="text-sm font-medium text-blue-900 mb-1">
                  Member Creation Process
                </p>
                <ul className="text-sm text-blue-700 space-y-1">
                  <li>• If the user already exists, they will be added immediately</li>
                  <li>• If the user is new, an invitation will be sent to their email</li>
                  <li>• Role assignments are enforced based on hierarchy levels</li>
                </ul>
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="flex items-center justify-end gap-3 pt-4 border-t border-gray-200">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              disabled={loading}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={loading || availableRoles?.length === 0}
              className="flex items-center gap-2"
            >
              {loading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  <span>Adding Member...</span>
                </>
              ) : (
                <>
                  <UserPlus className="w-4 h-4" />
                  <span>Add Member</span>
                </>
              )}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}